import React, { useEffect, useState } from 'react';
import { Download, UploadCloud, CheckCircle, AlertCircle } from 'lucide-react';
import './RestaurantExcelImport.css';

const BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const ROOT = `${BASE}/api/superadmin/`;

async function errorMessage(response) {
  const data = await response.json().catch(() => ({}));
  return typeof data.detail === 'string' ? data.detail : `Request failed (${response.status})`;
}

export default function RestaurantExcelImport() {
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantId, setRestaurantId] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);

  useEffect(() => {
    let active = true;
    async function loadRestaurants() {
      try {
        // Restaurant listing is paginated by the existing backend (25 per page).
        const all = [];
        for (let page = 1; page <= 400; page += 1) {
          const response = await fetch(`${ROOT}restaurants/?page=${page}`, { credentials: 'include' });
          if (!response.ok) throw new Error(await errorMessage(response));
          const data = await response.json();
          all.push(...(data.results || []));
          if (all.length >= (data.count || 0) || !(data.results || []).length) break;
        }
        if (active) setRestaurants(all);
      } catch (e) { if (active) setError(e.message); }
      finally { if (active) setLoadingRestaurants(false); }
    }
    loadRestaurants();
    return () => { active = false; };
  }, []);

  async function downloadTemplate() {
    setError(''); setBusy(true);
    try {
      const response = await fetch(`${ROOT}restaurants/excel-template/`, { credentials: 'include' });
      if (!response.ok) throw new Error(await errorMessage(response));
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'FoodHorizon_Restaurant_Import_Template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function submit(mode) {
    if (!restaurantId || !file) { setError('Select a restaurant and an Excel workbook or ZIP.'); return; }
    if (mode === 'import' && !preview?.valid) { setError('Preview the file before importing.'); return; }
    if (mode === 'import' && !window.confirm(`Import the validated records into ${restaurants.find(r => String(r.id) === restaurantId)?.name || 'this restaurant'}? This will add new records to the database.`)) return;
    setError(''); setBusy(true);
    if (mode === 'preview') { setPreview(null); setResult(null); }
    try {
      const csrfResponse = await fetch(`${ROOT}csrf/`, { credentials: 'include' });
      if (!csrfResponse.ok) throw new Error(await errorMessage(csrfResponse));
      const { csrfToken } = await csrfResponse.json();
      if (!csrfToken) throw new Error('Could not obtain CSRF token.');
      const body = new FormData();
      body.append('file', file);
      body.append('mode', mode);
      const response = await fetch(`${ROOT}restaurants/${encodeURIComponent(restaurantId)}/excel-import/`, {
        method: 'POST', credentials: 'include', headers: { 'X-CSRFToken': csrfToken }, body,
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      const data = await response.json();
      if (mode === 'preview') setPreview(data);
      else { setResult(data); setPreview(null); setFile(null); }
    } catch (e) { setError(e.message); if (mode === 'import') setPreview(null); }
    finally { setBusy(false); }
  }

  const counts = preview?.counts || result?.counts;
  return <section className="sa-panel sa-excel-import">
    <div className="sa-panel-head"><div><h2>Restaurant Excel + Images Import</h2><p className="sa-muted">Upload .xlsx for text-only records, or a .zip containing restaurant_data.xlsx and images/categories/ and images/items/.</p></div></div>
    <div className="sa-excel-actions"><button type="button" className="sa-secondary" onClick={downloadTemplate} disabled={busy}><Download size={17}/> Download Excel Template</button></div>
    <div className="sa-excel-form">
      <label>Restaurant
        <select value={restaurantId} disabled={busy || loadingRestaurants} onChange={e => { setRestaurantId(e.target.value); setPreview(null); setResult(null); setError(''); }}>
          <option value="">{loadingRestaurants ? 'Loading restaurants…' : 'Choose a restaurant'}</option>
          {restaurants.map(r => <option key={r.id} value={r.id}>{r.name} ({r.slug}) — {r.registered_tables}/{r.table_limit} tables</option>)}
        </select>
      </label>
      <label>Excel (.xlsx) or Excel + Images (.zip, up to 25 MB)
        <input type="file" accept=".xlsx,.zip" disabled={busy} onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); setError(''); }}/>
      </label>
      <button type="button" className="sa-primary" disabled={busy || !file || !restaurantId} onClick={() => submit('preview')}><UploadCloud size={17}/> {busy ? 'Working…' : 'Preview & Validate'}</button>
    </div>
    {error && <p className="sa-error" role="alert"><AlertCircle size={16}/> {error}</p>}
    {(preview?.images != null || result?.images != null) && <p className="sa-muted">Validated images: {preview?.images ?? result?.images}</p>}
    {counts && <div className="sa-excel-result"><h3><CheckCircle size={18}/> {result ? 'Import completed' : 'Validation passed'}</h3>
      <div className="sa-excel-counts">{Object.entries(counts).map(([name, count]) => <div key={name}><strong>{count}</strong><span>{name}</span></div>)}</div>
      <p className="sa-muted">{result ? 'Records were added successfully.' : 'Only new records will be added. Existing data will not be overwritten.'}</p>
      {preview?.valid && <button type="button" className="sa-primary" disabled={busy} onClick={() => submit('import')}>Confirm Import</button>}
    </div>}
    <p className="sa-muted sa-excel-help">Sheet names and column headers must match the downloaded template exactly. For images, add an image filename in the sheet and include that file in the corresponding ZIP folder. Duplicate records and table allocation limits are checked by the backend.</p>
  </section>;
}